import { caCities } from "@/data/site";

export type CityRegion = {
  id: string;
  label: string;
  blurb: string;
  slugs: string[];
};

/**
 * Service-area hubs. Cities are grouped the way we actually run crews and
 * routes, so the areas-served page reads like a real coverage map instead of
 * one long alphabetical list.
 */
export const cityRegions: CityRegion[] = [
  {
    id: "san-diego-metro",
    label: "East County & Bayside",
    blurb: "East County and bayside communities we cover on our regular weekly routes.",
    slugs: [
      "coronado",
      "winter-gardens",
      "pine-valley",
    ],
  },
  {
    id: "north-county-coastal",
    label: "North County Coastal",
    blurb: "Salt air, ocean humidity and older tile roofs – coastal homes need different details than inland ones.",
    slugs: ["la-jolla", "del-mar", "solana-beach", "encinitas", "carlsbad", "oceanside", "rancho-santa-fe"],
  },
  {
    id: "north-county-inland",
    label: "North County Inland",
    blurb: "Big tract roofs, strong sun exposure and plenty of room for solar plus battery backup.",
    slugs: ["scripps-ranch", "rancho-bernardo", "4s-ranch", "poway", "vista", "ramona", "valley-center", "fallbrook"],
  },
  {
    id: "riverside-county",
    label: "Riverside County",
    blurb: "Hot inland valleys where heat, wind and wildfire ratings drive the material choice.",
    slugs: ["temecula", "murrieta", "menifee", "lake-elsinore"],
  },
  {
    id: "orange-county",
    label: "Orange County",
    blurb: "South Orange County coastal and master-planned communities, HOA approvals included.",
    slugs: ["san-clemente", "san-juan-capistrano", "dana-point", "laguna-beach", "laguna-niguel", "ladera-ranch", "coto-de-caza", "rancho-mission-viejo", "mission-viejo"],
  },
];

export type GroupedRegion = CityRegion & {
  cities: typeof caCities;
};

/** Regions with their city objects resolved, plus any leftover city appended. */
export function getGroupedCities(): GroupedRegion[] {
  const bySlug = new Map(caCities.map((c) => [c.slug, c]));
  const used = new Set<string>();
  const groups: GroupedRegion[] = cityRegions.map((r) => {
    const cities = r.slugs
      .map((s) => bySlug.get(s))
      .filter((c): c is (typeof caCities)[number] => Boolean(c));
    cities.forEach((c) => used.add(c.slug));
    return { ...r, cities };
  });

  const leftovers = caCities.filter((c) => !used.has(c.slug));
  if (leftovers.length) {
    groups.push({
      id: "more-cities",
      label: "More Southern California Cities",
      blurb: "Additional cities on our regular service routes.",
      slugs: leftovers.map((c) => c.slug),
      cities: leftovers,
    });
  }
  return groups;
}
